import React from 'react';
import { AlertCircle, Check } from 'lucide-react';

const DeviceModeModal = ({
  showDeviceModeModal,
  pendingDeviceMode,
  deviceMode,
  deviceDimensions,
  selectedScalingOption,
  setSelectedScalingOption,
  setShowDeviceModeModal,
  setPendingDeviceMode,
  applyDeviceModeChange
}) => {
  if (!showDeviceModeModal || !pendingDeviceMode) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/20 max-w-lg w-full p-6">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Change Canvas Size?</h2>
            <p className="text-sm text-gray-600">
              Switching from <span className="font-semibold">{deviceDimensions[deviceMode].name}</span> to <span className="font-semibold">{deviceDimensions[pendingDeviceMode].name}</span> will change the canvas dimensions.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Current: {deviceDimensions[deviceMode].width} × {deviceDimensions[deviceMode].height}px → 
              New: {deviceDimensions[pendingDeviceMode].width} × {deviceDimensions[pendingDeviceMode].height}px
            </p>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-gray-900 mb-3">How should elements be adjusted?</p>
          
          <div className="space-y-3">
            <button
              onClick={() => setSelectedScalingOption('proportional')}
              className={`w-full text-left p-3 border-2 rounded-lg transition-colors ${
                selectedScalingOption === 'proportional'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  selectedScalingOption === 'proportional'
                    ? 'bg-primary-500'
                    : 'bg-gray-300'
                }`}>
                  {selectedScalingOption === 'proportional' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900">Scale Proportionally (Recommended)</p>
                  <p className="text-xs text-gray-600 mt-1">
                    All elements will resize and reposition proportionally to maintain the same relative layout.
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedScalingOption('center')}
              className={`w-full text-left p-3 border-2 rounded-lg transition-colors ${
                selectedScalingOption === 'center'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  selectedScalingOption === 'center'
                    ? 'bg-primary-500'
                    : 'bg-gray-300'
                }`}>
                  {selectedScalingOption === 'center' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900">Keep Sizes, Adjust Positions</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Elements keep their current sizes but positions adjust to stay centered.
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedScalingOption('none')}
              className={`w-full text-left p-3 border-2 rounded-lg transition-colors ${
                selectedScalingOption === 'none'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                  selectedScalingOption === 'none'
                    ? 'bg-primary-500'
                    : 'bg-gray-300'
                }`}>
                  {selectedScalingOption === 'none' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-semibold text-gray-900">Keep Everything As-Is</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Elements maintain exact positions and sizes (some may go off-screen).
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => {
              setShowDeviceModeModal(false);
              setPendingDeviceMode(null);
            }}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => applyDeviceModeChange(pendingDeviceMode, selectedScalingOption)}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceModeModal;
